import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { AlertTriangle, CheckCircle2, Info, X, XCircle, type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  DEFAULT_TOAST_APPEARANCE,
  type ToastStyle,
} from "@/lib/toastAppearance";

/** Semantic intents the toaster styles for. */
export type ToastVariant = "info" | "success" | "warning" | "error";
/** Accepted at call sites — legacy `default`/`destructive` are normalised to keep ~977 existing callers working. */
export type ToastVariantInput = ToastVariant | "default" | "destructive";

export function normalizeToastVariant(variant?: ToastVariantInput | null): ToastVariant {
  switch (variant) {
    case "success":
    case "warning":
    case "info":
    case "error":
      return variant;
    case "destructive":
      return "error";
    case "default":
    default:
      return "info";
  }
}

type ToastVariantMeta = {
  Icon: LucideIcon;
  /** Status accent rail fill (rail finish). */
  rail: string;
  /** Tinted icon chip (rail finish), light + dark. */
  chip: string;
  /** Soft coloured shadow (rail finish). */
  railShadow: string;
  /** Soft coloured shadow (aurora finish). */
  auroraShadow: string;
  /** Gradient stops for the aurora finish (dark enough for white text in both modes). */
  auroraStops: readonly [string, string, string];
};

export const TOAST_VARIANT_META: Record<ToastVariant, ToastVariantMeta> = {
  info: {
    Icon: Info,
    rail: "bg-violet-500",
    chip: "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-200",
    railShadow: "shadow-[0_14px_34px_-10px_rgba(124,58,237,0.4)]",
    auroraShadow: "shadow-[0_18px_42px_-10px_rgba(76,29,149,0.55)]",
    auroraStops: ["#5B21B6", "#6D28D9", "#7C3AED"],
  },
  success: {
    Icon: CheckCircle2,
    rail: "bg-emerald-500",
    chip: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200",
    railShadow: "shadow-[0_14px_34px_-10px_rgba(16,185,129,0.4)]",
    auroraShadow: "shadow-[0_18px_42px_-10px_rgba(5,120,85,0.5)]",
    auroraStops: ["#047857", "#059669", "#0D9488"],
  },
  warning: {
    Icon: AlertTriangle,
    rail: "bg-amber-500",
    chip: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200",
    railShadow: "shadow-[0_14px_34px_-10px_rgba(245,158,11,0.4)]",
    auroraShadow: "shadow-[0_18px_42px_-10px_rgba(180,83,9,0.5)]",
    auroraStops: ["#B45309", "#C2410C", "#B45309"],
  },
  error: {
    Icon: XCircle,
    rail: "bg-rose-500",
    chip: "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200",
    railShadow: "shadow-[0_14px_34px_-10px_rgba(225,29,72,0.4)]",
    auroraShadow: "shadow-[0_18px_42px_-10px_rgba(159,18,57,0.5)]",
    auroraStops: ["#9F1239", "#E11D48", "#BE123C"],
  },
};

const hexToRgba = (hex: string, alpha: number) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** Build the aurora gradient for a variant; translucent lowers stop alpha so the backdrop blur reads through. */
export function buildAuroraGradient(variant: ToastVariant, translucent: boolean): string {
  const [a, b, c] = TOAST_VARIANT_META[variant].auroraStops;
  const alpha = translucent ? 0.62 : 1;
  return `linear-gradient(135deg, ${hexToRgba(a, alpha)}, ${hexToRgba(b, alpha)} 55%, ${hexToRgba(c, alpha)})`;
}

/**
 * Full aurora surface background. Translucent variants composite the colour gradient
 * over a neutral dark base so white text stays AA-legible on LIGHT app pages and when
 * the backdrop blur is unavailable (unsupported browser / prefers-reduced-transparency).
 * The base is semi-transparent, so the frosted see-through character is preserved.
 */
export function buildAuroraBackground(variant: ToastVariant, translucent: boolean): string {
  const gradient = buildAuroraGradient(variant, translucent);
  return translucent ? `${gradient}, rgba(15, 17, 26, 0.55)` : gradient;
}

const ToastProvider = ({
  swipeDirection = "up",
  ...props
}: React.ComponentPropsWithoutRef<typeof ToastPrimitives.Provider>) => (
  <ToastPrimitives.Provider swipeDirection={swipeDirection} {...props} />
);
ToastProvider.displayName = ToastPrimitives.Provider.displayName;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "fixed left-1/2 top-0 z-[200] flex max-h-screen w-full -translate-x-1/2 flex-col items-center gap-2.5 p-4 sm:max-w-[420px]",
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastBaseVariants = cva(
  cn(
    "group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-2xl p-4 pr-[64px] transition-all",
    "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-4 data-[state=open]:zoom-in-95",
    "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=closed]:zoom-out-95",
    "data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)] data-[swipe=move]:transition-none",
    "data-[swipe=cancel]:translate-y-0 data-[swipe=end]:animate-out data-[swipe=end]:slide-out-to-top-full",
    "motion-reduce:transition-none motion-reduce:animate-none motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none",
  ),
  {
    variants: {
      surfaceStyle: {
        rail: "border border-border/60 bg-background text-foreground",
        aurora: "border border-white/15 text-white",
      },
    },
    defaultVariants: {
      surfaceStyle: "rail",
    },
  },
);

type ToastRootBaseProps = React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
  VariantProps<typeof toastBaseVariants>;

interface ToastProps extends Omit<ToastRootBaseProps, "surfaceStyle"> {
  variant?: ToastVariantInput;
  surfaceStyle?: ToastStyle;
  translucent?: boolean;
}

const Toast = React.forwardRef<React.ElementRef<typeof ToastPrimitives.Root>, ToastProps>(
  (
    {
      className,
      variant,
      surfaceStyle = DEFAULT_TOAST_APPEARANCE.style,
      translucent = DEFAULT_TOAST_APPEARANCE.translucent,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const resolvedVariant = normalizeToastVariant(variant);
    const meta = TOAST_VARIANT_META[resolvedVariant];
    const isAurora = surfaceStyle === "aurora";

    return (
      <ToastPrimitives.Root
        ref={ref}
        data-toast-surface=""
        data-toast-style={surfaceStyle}
        data-toast-translucent={translucent ? "true" : "false"}
        data-variant={resolvedVariant}
        className={cn(
          toastBaseVariants({ surfaceStyle: isAurora ? "aurora" : "rail" }),
          isAurora ? meta.auroraShadow : meta.railShadow,
          // Frosted surfaces stay legible: high-opacity base, blur is pure enhancement
          // (no-op on unsupported browsers; disabled under prefers-reduced-transparency via index.css).
          translucent && (isAurora ? "backdrop-blur-xl backdrop-saturate-150" : "bg-background/80 backdrop-blur-xl"),
          className,
        )}
        style={{
          ...(isAurora ? { background: buildAuroraBackground(resolvedVariant, translucent) } : {}),
          ...style,
        }}
        {...props}
      >
        {isAurora ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 -top-[40%] h-[160%] bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.28),_rgba(255,255,255,0)_58%)]"
          />
        ) : (
          <span aria-hidden="true" className={cn("absolute inset-y-0 left-0 w-1", meta.rail)} />
        )}
        {children}
      </ToastPrimitives.Root>
    );
  },
);
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastIcon = React.forwardRef<
  HTMLSpanElement,
  { variant?: ToastVariantInput; surfaceStyle?: ToastStyle; className?: string }
>(({ variant, surfaceStyle = DEFAULT_TOAST_APPEARANCE.style, className }, ref) => {
  const resolved = normalizeToastVariant(variant);
  const { Icon, chip } = TOAST_VARIANT_META[resolved];
  const isAurora = surfaceStyle === "aurora";

  return (
    <span
      ref={ref}
      aria-hidden="true"
      className={cn(
        "relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
        isAurora
          ? "border border-white/25 bg-white/15 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]"
          : chip,
        className,
      )}
    >
      <Icon className="h-[18px] w-[18px]" />
    </span>
  );
});
ToastIcon.displayName = "ToastIcon";

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-lg border px-3 text-[13px] font-medium ring-offset-background transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      "border-border/60 bg-transparent hover:bg-muted",
      "group-data-[toast-style=aurora]:border-white/30 group-data-[toast-style=aurora]:text-white group-data-[toast-style=aurora]:hover:bg-white/15",
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "flex h-6 w-6 items-center justify-center rounded-md opacity-70 transition-opacity hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring",
      "text-foreground/60 hover:text-foreground",
      "group-data-[toast-style=aurora]:text-white/75 group-data-[toast-style=aurora]:hover:text-white",
      className,
    )}
    aria-label="Close"
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("text-sm font-semibold leading-tight", className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn(
      "text-[13px] leading-snug text-muted-foreground group-data-[toast-style=aurora]:text-white/85",
      className,
    )}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastIcon,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
